import { useState } from "react";
import { CircleDot } from "lucide-react";
import { api, tokens } from "@/lib/api";
import type { AuthUser } from "@match/shared";

export function Login({
  onSignedIn,
  tenantName,
  logoUrl,
}: {
  onSignedIn: (u: AuthUser) => void;
  tenantName: string;
  /** Il logo del marchio: c'e' solo se l'indirizzo dice il tenant (dominio custom). */
  logoUrl: string | null;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(email, password);
      tokens.set(res.accessToken, res.refreshToken);
      onSignedIn(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-7 w-7 shrink-0 object-contain" aria-hidden />
          ) : (
            <span className="flex" aria-hidden>
              <CircleDot className="h-5 w-5 text-prop" strokeWidth={2.5} />
              <CircleDot className="-ml-2 h-5 w-5 text-broker" strokeWidth={2.5} />
            </span>
          )}
          <span className="text-base font-semibold tracking-tight text-strong">{tenantName}</span>
        </div>

        <h1 className="text-xl font-semibold tracking-tight">Accedi</h1>
        <p className="mt-1 text-sm text-muted">Usa le credenziali che ti sono state assegnate.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-sm text-muted">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="w-full rounded border border-line bg-raised px-3 py-2 text-sm outline-none focus:border-prop"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm text-muted">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full rounded border border-line bg-raised px-3 py-2 text-sm outline-none focus:border-prop"
            />
          </label>

          {error && (
            <p role="alert" className="rounded border border-loss/40 bg-loss/10 px-3 py-2 text-sm text-loss">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded bg-accent py-2 text-sm font-medium text-on-accent transition-opacity hover:bg-accent/90 disabled:opacity-50"
          >
            {busy ? "Verifica in corso…" : "Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}
